import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ReportDerivedClaim } from '../../types/reportClaim';
import type { SEDAmendment } from '../../types/sedAmendments';
import type { ReportClaim } from '../../types/citation';
import type { EvidenceConflict } from '../../services/evidenceConflict';
import type { RegistryResolver } from './types';
import { createFinancialAssumptionsResolver } from './financialAssumptionsResolver';
import { createProjectIntentResolver } from './projectIntentResolver';
import { createSedSectionResolver } from './sedSectionResolver';
import { createClaimResolver } from './claimResolver';
import { createConflictResolver } from './conflictResolver';
import { createSupplyChainResolver } from './supplyChainResolver';
import { createEvidenceResolver } from './evidenceResolver';
import { createAmendmentResolver } from './amendmentResolver';
import { createCitationResolver } from './citationResolver';
import { createNarrativeResolver } from './narrativeResolver';

export interface SiteResolverContext {
  site: SiteEntity;
  reports?: SiteRagReport[];
  sed?: SiteEvidenceDocument;
  claims?: ReportDerivedClaim[];
  amendments?: SEDAmendment[];
  conflicts?: EvidenceConflict[];
  reportClaims?: ReportClaim[];
}

/** Build all 10 registry resolvers from a site context bag. */
export function buildAllRegistryResolvers(ctx: SiteResolverContext): RegistryResolver[] {
  const { site } = ctx;
  const primaryReport = ctx.reports?.[0];

  return [
    createFinancialAssumptionsResolver({
      siteId: site.id,
      siteAssumptions: site.financialAssumptions,
      reportOverride: primaryReport?.financialAssumptionsOverride,
    }),
    createProjectIntentResolver({
      siteId: site.id,
      projectIntent: site.projectIntent,
    }),
    createSedSectionResolver({
      siteId: site.id,
      sed: ctx.sed,
    }),
    createClaimResolver({
      siteId: site.id,
      claims: ctx.claims,
    }),
    createConflictResolver({
      siteId: site.id,
      conflicts: ctx.conflicts,
    }),
    createSupplyChainResolver({
      siteId: site.id,
      sitePlan: site.supplyChainPlan,
      report: primaryReport,
    }),
    createEvidenceResolver({
      siteId: site.id,
      sed: ctx.sed,
    }),
    createAmendmentResolver({
      siteId: site.id,
      amendments: ctx.amendments,
    }),
    createCitationResolver({
      siteId: site.id,
      claims: ctx.reportClaims,
    }),
    createNarrativeResolver({
      siteId: site.id,
      reports: ctx.reports,
    }),
  ];
}
